import React from "react";
import clsx from "clsx";
import {
  LinkedAccount,
  UserType,
  APICategory,
  UnreleasedAPICategory,
} from "../../../../../../models/Entities";
import { Card, CopyTextField, Text } from "@merge-api/merge-javascript-shared";
import IDsCardSkeleton from "./components/IDsCardSkeleton";
import LinkedAccountID from "./components/LinkedAccountID";
import APITesterButton from "./components/APITesterButton";
import AddProductionLinkedAccountButton from "../../../AddProductionLinkedAccountButton";
import useAppContext from "../../../../../context/useAppContext";
import { MergeFlagFeature, useMergeFlag } from "../../../../../shared/hooks/useMergeFlag";

type OverviewIDsCardProps = {
  linkedAccount: LinkedAccount | null;
  integrationIssueComponent?: React.ReactNode;
  className?: string;
};

const OverviewIDsCard = ({
  linkedAccount,
  integrationIssueComponent,
  className,
}: OverviewIDsCardProps) => {
  const { user, isUserPrivileged } = useAppContext();
  const isMemberRole = user.type === UserType.member;

  const { enabled: isFilePickerDashboardEnabled } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_FILE_PICKER_DASHBOARD,
    organizationId: user.organization.id,
  });

  // skeleton
  if (!linkedAccount) {
    return <IDsCardSkeleton className={className} />;
  }

  return (
    <div className={clsx(className, "flex flex-col w-full gap-y-5")}>
      {!linkedAccount.is_test_account && (
        <AddProductionLinkedAccountButton linkedAccount={linkedAccount} />
      )}
      {!linkedAccount.is_test_account &&
        linkedAccount.category === APICategory.filestorage &&
        isFilePickerDashboardEnabled &&
        isUserPrivileged && (
          <AddProductionLinkedAccountButton linkedAccount={linkedAccount} isFilePicker />
        )}
      {!linkedAccount.is_test_account &&
        linkedAccount.category === APICategory.chat &&
        isFilePickerDashboardEnabled &&
        isUserPrivileged && (
          <AddProductionLinkedAccountButton linkedAccount={linkedAccount} isChannelPicker />
        )}
      {!linkedAccount.is_test_account &&
        linkedAccount.category === UnreleasedAPICategory.email &&
        isFilePickerDashboardEnabled &&
        isUserPrivileged && (
          <AddProductionLinkedAccountButton linkedAccount={linkedAccount} isEmailPicker />
        )}
      <APITesterButton linkedAccount={linkedAccount} />

      {/* integration issue */}
      {integrationIssueComponent}
      <Card className="overflow-hidden">
        {/* header */}
        <div className="px-4 py-3 border-b-solid border-b-gray-10 border-b-[1px]">
          <Text variant="h5">Account details</Text>
        </div>
        {/* Linked Account ID, connection ID, destination ID, and account token
            are tenant-scoped identifiers — masked from PostHog autocapture and
            from the Frigade Assistant DOM snapshot. */}
        <div className="ph-no-capture flex flex-col gap-3 p-4" data-fr-ignore>
          {/* Linked Account Id */}
          <LinkedAccountID
            linkedAccountId={linkedAccount.id}
            hasIssue={!!integrationIssueComponent}
          />

          {/* Sync Connection Id (used for data warehouse) */}
          {linkedAccount.sync_connection_id && (
            <div className="flex flex-col gap-2">
              <Text variant="h6" className="mb-2">
                Connection ID
              </Text>
              <CopyTextField text={linkedAccount.sync_connection_id} />
            </div>
          )}

          {/* Destination ID (used for destinations) */}
          {linkedAccount.destination_id && (
            <div className="flex flex-col gap-2">
              <Text variant="h6" className="mb-2">
                Destination ID
              </Text>
              <CopyTextField text={linkedAccount.destination_id} />
            </div>
          )}

          {/* Account Token */}
          {!isMemberRole && linkedAccount.account_token && (
            <div className="flex flex-col gap-2">
              <Text variant="h6">Account token</Text>
              <CopyTextField text={linkedAccount.account_token} secret />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
export default OverviewIDsCard;
